import { TaskProgress } from "@/types/LCAoM/TaskProgress";
import i18n from "@i18n/i18n";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import { GridOverlayProps } from "@mui/x-data-grid-premium";

/**
 * Props for the TaskProgressOverlay component.
 * Extends GridOverlayProps to be compatible with MUI DataGrid overlay slots.
 */
export interface TaskProgressOverlayProps extends Partial<GridOverlayProps> {
  /** Current task progress, or null to hide the overlay */
  progress: TaskProgress | null;
}

// Shimmer animation matching LoadingOverlay
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

/**
 * DataGrid overlay component for showing task operation progress.
 * Used for operations like material assignment, removal, etc.
 *
 * Matches the look and feel of LoadingOverlay for visual consistency.
 *
 * @see https://v7.mui.com/x/react-data-grid/overlays/#loading-overlay
 *
 * Usage:
 * ```tsx
 * // As a conditional overlay wrapper
 * {taskProgress && <TaskProgressOverlay progress={taskProgress} />}
 * ```
 */
export function TaskProgressOverlay({ progress }: TaskProgressOverlayProps) {
  const theme = useTheme();

  if (!progress) {
    return null;
  }

  const isComplete = progress.phase === "complete";
  const hasProgress = progress.total > 0;
  const progressValue = progress.percent;

  // Hide overlay when complete
  if (isComplete) {
    return null;
  }

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        height: "100%",
        backgroundColor: alpha(theme.palette.background.default, 0.9),
        backdropFilter: "blur(4px)",
        zIndex: 10,
      }}
    >
      <Stack alignItems="center" spacing={2} sx={{ minWidth: 200 }}>
        {/* Progress indicator - either determinate bar or animated shimmer */}
        {hasProgress ? (
          <Box sx={{ width: "100%", maxWidth: 280 }}>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  transition: "transform 0.2s ease",
                },
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: 48,
              height: 3,
              borderRadius: 1.5,
              background: `linear-gradient(90deg, 
                ${alpha(theme.palette.primary.main, 0.3)} 0%, 
                ${theme.palette.primary.main} 50%, 
                ${alpha(theme.palette.primary.main, 0.3)} 100%)`,
              backgroundSize: "200% 100%",
              animation: `${shimmer} 1.2s ease-in-out infinite`,
            }}
          />
        )}

        {/* Status text */}
        <Stack alignItems="center" spacing={0.5}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 500,
              letterSpacing: "0.02em",
            }}
          >
            {progress.label}
          </Typography>

          {/* Progress details (processed/total counts) */}
          {hasProgress && (
            <Typography
              variant="caption"
              sx={{
                color: alpha(theme.palette.text.secondary, 0.7),
                fontWeight: 400,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {i18n.t("task-progress-overlay.progress", {
                processed: progress.processed.toLocaleString(),
                total: progress.total.toLocaleString(),
                label: progress.subLabel || i18n.t("task-progress-overlay.items"),
              })}
            </Typography>
          )}

          {/* Percentage */}
          {hasProgress && (
            <Typography
              variant="caption"
              sx={{
                color: alpha(theme.palette.text.disabled, 0.8),
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {i18n.t("task-progress-overlay.percentComplete", { percent: progressValue })}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}

export default TaskProgressOverlay;
